import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { constructUrl, getValidPaginatorSize } from '@/lib/utils';
import { AdvisorModel, ProgramModel } from '@/lib/models';
import { accountService, advisorService } from '@/lib/services';
import { useAccount, useNavigationConfig, useQuery } from '@/hooks';
import { Advisor, AsyncPage, LinkBox, Select, Loader, LoadingButton } from '@/components';

export const AdvisoryTeam = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { account } = useAccount();
	const query = useQuery();
	const programId = useMemo(() => query.get('programId'), [query]);
	const { navigationConfig } = useNavigationConfig();

	const [programOptions, setProgramOptions] = useState<ProgramModel[]>([]);
	const [advisors, setAdvisors] = useState<AdvisorModel[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const size = useMemo(() => getValidPaginatorSize(query), [query]);
	const pageRef = useRef(0);
	const [loading, setLoading] = useState(false);

	const fetchPrograms = useCallback(async () => {
		if (!account) {
			throw new Error('account is undefined');
		}

		const response = await accountService.getPrograms(account.accountId, { activeOnly: true }).fetch();
		setProgramOptions(response.programs);
	}, [account]);

	const fetchData = useCallback(async () => {
		setLoading(true);
		const response = await advisorService
			.getAdvisoryTeam({
				...(programId && { programId }),
				size: parseInt(size, 10),
                page: pageRef.current,
			})
			.fetch();

		setTotalCount(response.totalCount);
		setAdvisors((previousValue) => {
			if (pageRef.current === 0) {
				return response.advisors;
			}
			return previousValue.concat(response.advisors);
		});
		setLoading(false);
	}, [programId, size]);

	const handleProgramSelectChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { programId: currentTarget.value }, search));
	};

	const handlePageSizeChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { size: currentTarget.value }, search));
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		fetchData();
	};

	return (
		<Container className="py-13">
			<Helmet>
				<title>Hackett Connect - Advisory Team</title>
			</Helmet>
			<Row className="mb-6">
				<Col>
					<h1 className="mb-0 text-primary">Meet Your Team</h1>
				</Col>
			</Row>
			<Row>
				<Col xs={8}>
					<AsyncPage fetchData={fetchData}>
						<div className="mb-6 d-flex align-items-center justify-content-end">
							<div className="d-flex align-items-center">
								<Form.Label className="mb-0 mr-2">Page Size</Form.Label>
								<Select variant="secondary" value={size} onChange={handlePageSizeChange}>
									<option value="10">10</option>
									<option value="20">20</option>
									<option value="50">50</option>
								</Select>
							</div>
						</div>
						{!loading && advisors.length === 0 && (
							<p className="mb-2 text-muted text-center">
								<strong>No Results</strong>
							</p>
						)}
						{advisors.map((advisor, index) => {
							const isLast = advisors.length - 1 === index;

							return (
								<React.Fragment key={advisor.advisorId}>
									<Advisor
										advisorId={advisor.advisorId}
										avatarUrl={advisor.imageUrl}
										jobTitle={advisor.role}
										name={advisor.name}
										specialties={advisor.position}
										bio={advisor.description}
										bookingsUrl={
											navigationConfig?.allowAccessToInquiries ? advisor.bookingsUrl : undefined
										}
										hasPublications={advisor.hasPublications}
									/>
									{!isLast && <hr className="my-10" />}
								</React.Fragment>
							);
						})}
						{loading && advisors.length <= 0 && (
							<div className="mb-8 d-flex justify-content-center">
								<Loader />
							</div>
						)}
						<hr className="mt-8 mb-8" />
						<div className="d-flex align-items-center justify-content-end">
							{advisors.length > 0 && (
								<span className="text-muted mr-4">
									Showing 1-{advisors.length} of {totalCount}
								</span>
							)}
							{advisors.length < totalCount && (
								<LoadingButton
									isLoading={loading}
									variant="outline-info"
									onClick={handleShowMoreButtonClick}
								>
									Show {size} More
								</LoadingButton>
							)}
						</div>
					</AsyncPage>
				</Col>
				<Col xs={4}>
					<Card className="mb-2">
						<Card.Body>
							<div className="mb-4 d-flex align-items-center justify-content-between">
								<Card.Title className="mb-0">Filters</Card.Title>
							</div>
							<AsyncPage fetchData={fetchPrograms}>
								<Form.Group>
									<Form.Label>Program</Form.Label>
									<Select value={programId ?? ''} onChange={handleProgramSelectChange}>
										<option value="">All Programs</option>;
										{programOptions.map((po) => {
											return (
												<option key={po.programId} value={po.programId}>
													{po.name}
												</option>
											);
										})}
									</Select>
								</Form.Group>
							</AsyncPage>
						</Card.Body>
					</Card>
					<LinkBox
						sections={[
							{
								title: "Can't find what you're looking for?",
								links: [{ title: 'Contact Hackett', to: '/contact', showIcon: true }],
							},
						]}
					/>
				</Col>
			</Row>
		</Container>
	);
};
